/**
 * Grade level display utilities.
 * Handles Pre-K 3 (grade -2), Pre-K 4 (grade -1), Kindergarten (grade 0), and grades 1-12.
 */

/** Short format: "PK3", "PK4", "K", "1", "2", ... "12", or "-" for null/undefined */
export function formatGradeShort(grade: number | null | undefined): string {
  if (grade === null || grade === undefined) return '-';
  if (grade === -2) return 'PK3';
  if (grade === -1) return 'PK4';
  return grade === 0 ? 'K' : String(grade);
}

/** Long format: "Pre-K 3 (Age 3)", "Pre-K 4 (Age 4)", "Kindergarten", "Grade 1", ... "Grade 12", or "-" for null/undefined */
export function formatGradeLong(grade: number | null | undefined): string {
  if (grade === null || grade === undefined) return '-';
  if (grade === -2) return 'Pre-K 3 (Age 3)';
  if (grade === -1) return 'Pre-K 4 (Age 4)';
  return grade === 0 ? 'Kindergarten' : `Grade ${grade}`;
}

/**
 * i18n-aware grade label for parent-facing components.
 * Caller must have the 'common' namespace loaded
 * (e.g., useTranslation('common') or useTranslation(['parent', 'common'])).
 */
export function formatGradeI18n(
  grade: number | null | undefined,
  t: (key: string, options?: Record<string, unknown>) => string,
): string {
  if (grade == null) return '-';
  if (grade === -2) return t('common:preK3');
  if (grade === -1) return t('common:preK4');
  if (grade === 0) return t('common:kindergarten');
  return t('common:grade', { number: grade });
}

/** Ordinal format: "PK3", "PK4", "K", "1st", "2nd", "3rd", "4th", ... "12th", or "-" for null/undefined */
export function formatGradeOrdinal(grade: number | null | undefined): string {
  if (grade === null || grade === undefined) return '-';
  if (grade === -2) return 'PK3';
  if (grade === -1) return 'PK4';
  if (grade === 0) return 'K';
  const suffixes = ['th', 'st', 'nd', 'rd'];
  const mod100 = Math.abs(grade) % 100;
  const suffix = mod100 >= 11 && mod100 <= 13 ? 'th' : suffixes[Math.abs(grade) % 10] || 'th';
  return `${grade}${suffix}`;
}
